import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { FiX } from "react-icons/fi";
import HelperService from "../../Services/HelperService";
import WebService from "../../Services/WebService";
import { Improvement } from "./profile_analysis_interfaces";

interface UserInputModalProps {
  show: boolean;
  onHide: () => void;
  item: Improvement | null;
  sectionPage?: string;
  businessInfoId?: string | number | null;
  businessId?: string | number | null;
  businessUuid?: string | null;
  onSuccess?: () => void;
}

const formatKeyFromTitle = (title?: string): string => {
  if (!title) return "user_input";
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

const extractUserInputString = (val: any): string => {
  if (!val) return "";
  if (typeof val === "string") return val.trim();
  if (typeof val === "object") {
    const vals = Object.values(val).filter(
      (v) => typeof v === "string" && v.trim()
    );
    return vals.join(", ");
  }
  return String(val);
};

const UserInputModal: React.FC<UserInputModalProps> = ({
  show,
  onHide,
  item,
  sectionPage,
  businessInfoId,
  businessId,
  businessUuid,
  onSuccess,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [existingUserVal, setExistingUserVal] = useState("");
  const [inputKey, setInputKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (show && item) {
      const rawField =
        item.field ||
        (item as any)?.field_name ||
        item.key ||
        item.field_key ||
        item.input_key;

      const derivedKey =
        typeof rawField === "string" && rawField.trim()
          ? rawField.trim()
          : Array.isArray(rawField) && rawField.length > 0 && typeof rawField[0] === "string"
            ? rawField[0].trim()
            : formatKeyFromTitle(item.title);

      const existingInput =
        extractUserInputString(item.user_input) ||
        extractUserInputString((item as any)?.userInput) ||
        (typeof item.input_value === "string" ? item.input_value.trim() : "") ||
        (typeof item.value === "string" ? item.value.trim() : "");

      setInputKey(derivedKey);
      setExistingUserVal(existingInput);
      setInputValue(existingInput);
      setValidationError("");
    }
  }, [show, item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) {
      setValidationError("Please enter details.");
      return;
    }

    setLoading(true);
    setValidationError("");

    try {
      const bInfoId =
        businessInfoId ??
        (item as any)?.business_info_id ??
        (item as any)?.businessId ??
        businessId ??
        localStorage.getItem("business_info_id") ??
        localStorage.getItem("business_id") ??
        null;
      const bUuid = businessUuid || localStorage.getItem("business_uuid") || "";
      const targetId = bInfoId || bUuid || "";
      const action = targetId ? `set-user-input/${targetId}` : "set-user-input";

      const finalKey =
        typeof item?.field === "string" && item.field.trim()
          ? item.field.trim()
          : inputKey || formatKeyFromTitle(item?.title);

      const payload: any = {
        key: finalKey,
        value: inputValue.trim(),
        user_input: {
          [finalKey]: inputValue.trim(),
        },
      };

      if (bInfoId) {
        payload.business_info_id = bInfoId;
        payload.business_id = bInfoId;
      }
      if (bUuid) payload.business_uuid = bUuid;

      const response: any = await WebService.postAPI({
        action,
        body: payload,
        isShowError: true,
      });

      toast.success(response?.message || "Details saved successfully!");
      if (onSuccess) onSuccess();
      onHide();
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save details. Please try again.";
      setValidationError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  return (
    <Modal
      show={show}
      onHide={loading ? undefined : onHide}
      centered
      size="sm"
      contentClassName="border-0 shadow"
      style={{ zIndex: 1065 }}
    >
      <div
        style={{
          borderRadius: 14,
          overflow: "hidden",
          background: "#ffffff",
          width: "100%",
          fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "14px 16px 10px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ paddingRight: 10 }}>
            <h6
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
              }}
            >
              {item.title || "Add Details"}
            </h6>
            {item.description && (
              <p
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  margin: "3px 0 0",
                  lineHeight: 1.4,
                }}
              >
                {item.description}
              </p>
            )}
          </div>
          <button
            onClick={onHide}
            disabled={loading}
            aria-label="Close"
            style={{
              border: "none",
              background: "transparent",
              color: "#94a3b8",
              cursor: loading ? "not-allowed" : "pointer",
              padding: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Body */}
        <Modal.Body style={{ padding: "14px 16px" }}>
          {/* If user previously provided input, display it prominently */}
          {existingUserVal && (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderLeft: "3.5px solid #66C15E",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  marginBottom: 5,
                }}
              >
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: 0.6,
                    color: "#15803d",
                  }}
                >
                  Current Submitted Details
                </span>
                {item.explanation_status && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      padding: "2px 7px",
                      borderRadius: 999,
                      background:
                        item.explanation_status === "needs_evidence"
                          ? "#fffbeb"
                          : item.explanation_status === "resolved"
                            ? "#ecfdf5"
                            : "#f1f5f9",
                      color:
                        item.explanation_status === "needs_evidence"
                          ? "#b45309"
                          : item.explanation_status === "resolved"
                            ? "#047857"
                            : "#475569",
                      border:
                        item.explanation_status === "needs_evidence"
                          ? "1px solid #fde68a"
                          : item.explanation_status === "resolved"
                            ? "1px solid #a7f3d0"
                            : "1px solid #e2e8f0",
                    }}
                  >
                    {item.explanation_status === "needs_evidence"
                      ? "⚠️ Evidence Required"
                      : item.explanation_status === "review_unavailable"
                        ? "⏳ Review Pending"
                        : item.explanation_status === "resolved"
                          ? "✓ Verified"
                          : HelperService.ProperCase(item.explanation_status)}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1e293b",
                  fontStyle: "italic",
                  lineHeight: 1.45,
                }}
              >
                "{existingUserVal}"
              </div>
              {item.explanation_reason && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: "#64748b",
                    marginTop: 7,
                    lineHeight: 1.4,
                    borderTop: "1px dashed #e2e8f0",
                    paddingTop: 6,
                  }}
                >
                  <strong style={{ color: "#334155" }}>AI Feedback: </strong>
                  {item.explanation_reason}
                </div>
              )}
            </div>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#334155",
                  marginBottom: 6,
                }}
              >
                {existingUserVal
                  ? "Update your details or provide additional evidence:"
                  : "Enter details:"}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                autoFocus
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  if (validationError) setValidationError("");
                }}
                placeholder={
                  existingUserVal
                    ? "Enter updated details or justification..."
                    : "Enter details here..."
                }
                disabled={loading}
                style={{
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  padding: "8px 12px",
                  fontSize: 13,
                  lineHeight: 1.45,
                  resize: "none",
                  boxShadow: "none",
                }}
              />
              {validationError && (
                <div style={{ color: "#dc2626", fontSize: 11.5, marginTop: 4 }}>
                  {validationError}
                </div>
              )}
            </Form.Group>

            {/* Footer */}
            <div className="d-flex justify-content-end align-items-center gap-2">
              <Button
                variant="light"
                size="sm"
                onClick={onHide}
                disabled={loading}
                style={{
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "5px 12px",
                  color: "#64748b",
                  border: "1px solid #e2e8f0",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={loading || !inputValue.trim()}
                style={{
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "5px 16px",
                  background: "#66C15E",
                  border: "none",
                  color: "#ffffff",
                }}
              >
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : existingUserVal ? (
                  "Update Details"
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default UserInputModal;
